"use client";

import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, AnimatePresence } from "motion/react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FaqSection from "@/components/FaqSection";
import Reveal from "@/components/Reveal";
import VideoBackdrop from "@/components/VideoBackdrop";
import PhotoCarousel from "@/components/PhotoCarousel";
import CorporateCollabCard from "@/components/CorporateCollabCard";
import WhatsAppCommunityCard from "@/components/WhatsAppCommunityCard";
import InstagramTeaser from "@/components/InstagramTeaser";
import SerifAccent from "@/components/SerifAccent";
import { useTranslations } from "@/i18n/LocaleProvider";
import { SERVICES } from "@/lib/services";
import type { ServiceListing } from "@/lib/services";
import { DEVINA_GALLERY_IMAGES } from "@/lib/galleryImages";
import { useBundleDiscount } from "@/lib/useBundleDiscount";
import { SHOW_PRICING } from "@/lib/featureFlags";

export default function Home() {
  useEffect(() => {
    if (!window.location.hash) return;
    const id = window.location.hash.slice(1);
    const scrollToHash = () => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    scrollToHash();
    // Sections below the fold (FaqSection, InstagramSection, etc.) can still shift layout
    // as images finish loading right after mount -- one retry after that settles catches a
    // hash link that would otherwise land short of its target.
    const timer = setTimeout(scrollToHash, 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Header />
      <main className="flex-1">
        <Hero />
        <Services />
        <GroupClasses />
        <About />
        <Testimonials />
        <WhatsAppCommunity />
        <FaqSection />
        <InstagramSection />
        <Contact />
      </main>
      <Footer />
    </>
  );
}

// Behind every hero heading/subtitle/CTA -- a fixed shadow rather than a Tailwind utility, so
// legibility never depends on whatever's playing behind it in any given video frame, on top of
// the tint and gradient below.
const heroTextShadow = { textShadow: "0 2px 20px rgba(0,0,0,0.45)" };

function Hero() {
  const { t } = useTranslations();
  return (
    // Negative top margin pulls the video up underneath the sticky, transparent-at-top header
    // (see Header.tsx) instead of the header consuming its own row and pushing the video down --
    // the header still occupies normal flow (unchanged sticky/scroll behavior), this just slides
    // the hero visually behind it so the video reads full-bleed from the true top of the page.
    // 100dvh, not h-screen's 100vh -- on a real phone, 100vh is measured against the *largest*
    // possible viewport (address bar collapsed), so on first load (address bar still visible)
    // the hero's actual bottom edge sits below what's currently visible, cutting off "Book a
    // session" until the page is scrolled. dvh tracks the real, current viewport instead.
    <section className="relative -mt-[72px] h-[100dvh] overflow-hidden">
      <VideoBackdrop
        webmSrc="/videos/home-hero.webm"
        mp4Src="/videos/home-hero.mp4"
        poster="/videos/home-hero-poster.jpg"
        alt="Devina Prasad leading a hoop dance session"
        priority
        tintClassName="bg-black/35"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-black/25" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="relative mx-auto flex h-full max-w-[1440px] flex-col justify-end gap-5 px-8 pb-6 pt-10"
      >
        <div>
          <h1
            style={heroTextShadow}
            className="font-display text-4xl leading-none text-white sm:text-6xl"
          >
            {t("home.hero.title")}{" "}
            {/* whitespace-nowrap: "mean it." is two words -- without this, a narrow line can
                wrap between them, landing "mean" on one line and "it." alone on the next. */}
            <span className="whitespace-nowrap">
              <SerifAccent>{t("home.hero.titleAccent")}</SerifAccent>.
            </span>
          </h1>
          <p style={heroTextShadow} className="mt-4 max-w-xl text-lg text-white/90">
            {t("home.hero.subtitlePrefix")}{" "}
            <span className="font-semibold text-white">{t("home.hero.subtitleName")}</span>.{" "}
            {t("home.hero.subtitleSuffix")}
          </p>
          {/* Its own slide-up-and-fade, timed to land just after the heading/subtitle settle --
              same staggered-entrance feel as socialcorn.com's hero CTA, not a scroll trigger. */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.35 }}
            className="mt-6 flex flex-wrap gap-4"
          >
            <Link
              href="/book"
              className="rounded-xl bg-accent px-7 py-3 font-semibold text-accent-foreground transition hover:scale-[1.03] hover:opacity-90"
            >
              {t("home.hero.cta")}
            </Link>
            <Link
              href="/services"
              className="hidden rounded-xl border border-white/50 px-7 py-3 font-semibold text-white transition hover:border-white hover:bg-white/10 sm:block"
            >
              {t("home.hero.secondaryCta")}
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}

function Services() {
  const { t } = useTranslations();
  const bundlePercent = useBundleDiscount();
  return (
    <section id="services" className="bg-background">
      <div className="mx-auto max-w-[1440px] px-8 py-10 sm:py-16">
        <Reveal className="mb-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-accent">
              {t("home.services.eyebrow")}
            </p>
            {SHOW_PRICING && (
              <span className="rounded-full bg-accent-2 px-3 py-1 text-xs font-semibold text-accent-2-foreground">
                {t("home.services.bundleBadge", { percent: bundlePercent })}
              </span>
            )}
          </div>
          <div className="max-w-xl">
            <h2 className="mt-3 font-display text-3xl sm:text-4xl">{t("home.services.title")}</h2>
            <p className="mt-4 text-foreground/70">{t("home.services.subtitle")}</p>
          </div>
        </Reveal>
        <ServicesSlider />
        <div className={`mt-6 flex flex-wrap items-center gap-4 ${SHOW_PRICING ? "justify-between" : "justify-end"}`}>
          {SHOW_PRICING && <p className="text-xs text-muted">{t("home.services.depositFootnote")}</p>}
          <Link href="/services" className="text-sm font-semibold text-accent hover:underline">
            {t("home.services.seeAll")} →
          </Link>
        </div>
      </div>
    </section>
  );
}

const SERVICES_ROTATION_MS = 5000;
// All of them, not just a preview slice -- unlike the old static grid (which showed 3 and
// pointed elsewhere for the rest), the slider actually has somewhere to put every service, and a
// 3-item deck wouldn't have anywhere to slide to on desktop (3 per page = 1 page = never moves).
const SERVICES_SLIDER_ITEMS = SERVICES;

function ServiceCard({ service }: { service: ServiceListing }) {
  const { t } = useTranslations();
  const depositAmount = Math.round((service.priceInr * service.depositPercent) / 100);
  return (
    <Link
      href={`/book?service=${service.slug}`}
      className="group flex h-full flex-col justify-between overflow-hidden rounded-2xl border border-foreground/10 bg-white/40 transition hover:border-accent/40 hover:bg-white/70"
    >
      <div className="relative h-32 w-full overflow-hidden">
        <Image
          src={service.image}
          alt={service.imageAlt}
          fill
          sizes="(max-width: 640px) 100vw, 25vw"
          style={{ objectPosition: service.imagePosition }}
          className="object-cover transition duration-300 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col justify-between p-5">
        <div>
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="min-w-0 break-words font-display text-lg">
              {t(`home.services.items.${service.slug}.name`)}
            </h3>
            {SHOW_PRICING && (
              <span className="whitespace-nowrap font-semibold text-accent">
                ₹{service.priceInr.toLocaleString("en-IN")}
              </span>
            )}
          </div>
          <div className="mt-1 flex items-center justify-between gap-2 text-sm">
            <span className="text-muted">{service.duration}</span>
            {SHOW_PRICING && service.depositPercent < 100 && (
              <span className="inline-flex whitespace-nowrap rounded-full bg-accent-2 px-2.5 py-0.5 text-xs font-semibold text-accent-2-foreground">
                {t("home.services.depositBadge", {
                  amount: depositAmount.toLocaleString("en-IN"),
                })}
              </span>
            )}
          </div>
        </div>
        <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-accent transition-all group-hover:gap-2">
          {t("home.services.bookThis")}
        </span>
      </div>
    </Link>
  );
}

function ServicesSlider() {
  const perPage = useResponsiveSliderPerPage(3);
  const { page, visibleItems } = useAutoSlider(SERVICES_SLIDER_ITEMS, perPage, SERVICES_ROTATION_MS);
  return (
    <SlidingGrid page={page} gridClassName={`grid gap-5 ${perPage === 3 ? "lg:grid-cols-3" : "grid-cols-1"}`}>
      {visibleItems.map((service) => (
        <ServiceCard key={service.slug} service={service} />
      ))}
    </SlidingGrid>
  );
}

// A description-only teaser -- no live seat/schedule data fetched here, unlike /batches itself.
// Keeping this section static avoids a Firestore round trip on the homepage for content that's
// only ever meant to point people to the real, live batch list.
function GroupClasses() {
  const { t } = useTranslations();
  const points = [
    t("home.groupClasses.point1"),
    t("home.groupClasses.point2"),
    t("home.groupClasses.point3"),
  ];
  return (
    <section className="bg-background-alt">
      <div className="mx-auto max-w-[1440px] px-8 py-12 sm:py-16 lg:py-20">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:items-center sm:gap-12">
          <Reveal>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-accent">
              {t("home.groupClasses.eyebrow")}
            </p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl">
              {t("home.groupClasses.title")} <SerifAccent>{t("home.groupClasses.titleAccent")}</SerifAccent>.
            </h2>
            <p className="mt-4 text-foreground/70">{t("home.groupClasses.subtitle")}</p>
            <ul className="mt-6 space-y-3">
              {points.map((point) => (
                <li key={point} className="flex items-start gap-3 text-foreground/80">
                  <span className="mt-2.5 h-px w-6 shrink-0 bg-accent-2" aria-hidden />
                  {point}
                </li>
              ))}
            </ul>
            <Link
              href="/batches"
              className="mt-8 inline-flex items-center gap-1 rounded-xl bg-accent px-7 py-3 font-semibold text-accent-foreground transition hover:opacity-90"
            >
              {t("home.groupClasses.cta")}
            </Link>
          </Reveal>
          {/* lg:max-h caps this at a height that keeps the whole section within one common
              desktop viewport (~900px tall) alongside its own py-20 -- aspect-[4/5] alone (no
              cap) makes this column, not the text beside it, the tallest thing in the row,
              pushing the section past a full screen. Left uncapped below `lg`: a stacked
              single-column mobile/tablet layout has no such one-viewport goal, and the full
              portrait crop reads better there anyway. */}
          <Reveal
            delay={0.1}
            className="relative aspect-[4/5] w-full rounded-2xl shadow-2xl lg:max-h-[600px]"
          >
            <div className="absolute inset-0 overflow-hidden rounded-2xl">
              <Image
                src="/images/gallery-6.jpg"
                alt="Devina in a yoga backbend, taught live in group batches"
                fill
                sizes="(max-width: 640px) 100vw, 50vw"
                className="object-cover"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function About() {
  const { t } = useTranslations();
  return (
    <section id="about" className="bg-background">
      <div className="mx-auto max-w-[1440px] px-8 py-12 sm:py-16 lg:py-20">
        <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 sm:items-center">
          <Reveal className="relative aspect-[4/5] max-h-[75vh] w-full overflow-hidden rounded-2xl">
            <VideoBackdrop
              webmSrc="/videos/about-hoop.webm"
              mp4Src="/videos/about-hoop.mp4"
              poster="/videos/about-hoop-poster.jpg"
              alt="Devina Prasad performing an LED hoop routine"
              sizes="(max-width: 640px) 100vw, 50vw"
              tintClassName="bg-black/25"
              lazy
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent" />
            <Link
              href="/book"
              className="absolute inset-x-6 bottom-6 flex items-center justify-center rounded-xl bg-accent px-6 py-3 text-center font-semibold text-accent-foreground shadow-lg transition hover:scale-[1.02] hover:opacity-90"
            >
              {t("home.about.cta")}
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-accent">
              {t("home.about.eyebrow")}
            </p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl">{t("home.about.title")}</h2>
            <p className="mt-4 text-foreground/70">{t("home.about.body")}</p>
            <Link
              href="/about"
              className="mt-4 inline-flex text-sm font-semibold text-accent hover:underline"
            >
              {t("home.about.readMore")}
            </Link>
            <div className="mt-8">
              <PhotoCarousel images={DEVINA_GALLERY_IMAGES} />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

// Placeholder testimonials pending real ones from Devina's students -- written to be
// representative of the three service types, not attributed to real people. Replace with
// real quotes (and real names, with permission) as soon as Devina can gather them.
// avatarBg: distinct on-brand color per placeholder person, used behind their initials below --
// deliberately NOT a stock photo of an actual (uninvolved) person, which would misrepresent a
// fictional reviewer as a real one. Swap for a real photo the moment there's a real testimonial.
const MIXED_AVATAR_BG = "color-mix(in srgb, var(--accent) 55%, var(--accent-2) 45%)";
const TESTIMONIALS = [
  { key: "priya", avatarBg: "var(--accent)" },
  { key: "ananya", avatarBg: "var(--accent-2)" },
  { key: "meera", avatarBg: MIXED_AVATAR_BG },
  { key: "rohit", avatarBg: "var(--accent)" },
  { key: "kavya", avatarBg: "var(--accent-2)" },
  { key: "sneha", avatarBg: MIXED_AVATAR_BG },
  { key: "aditi", avatarBg: "var(--accent)" },
  { key: "neha", avatarBg: "var(--accent-2)" },
  { key: "pooja", avatarBg: MIXED_AVATAR_BG },
];

function Testimonials() {
  const { t } = useTranslations();
  return (
    <section className="bg-background-alt">
      <div className="mx-auto max-w-[1440px] px-8 py-12 sm:py-16 lg:py-20">
        <Reveal className="mb-12 max-w-xl">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-accent">
            {t("home.testimonials.eyebrow")}
          </p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl">{t("home.testimonials.title")}</h2>
        </Reveal>
        <TestimonialsSlider />
      </div>
    </section>
  );
}

const TESTIMONIAL_ROTATION_MS = 5500;

function TestimonialCard({ item }: { item: (typeof TESTIMONIALS)[number] }) {
  const { t } = useTranslations();
  const { key, avatarBg } = item;
  const name = t(`home.testimonials.items.${key}.name`);
  const initials = name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2);
  return (
    <div className="flex h-full min-h-[220px] flex-col justify-between rounded-2xl border border-foreground/10 bg-white/40 p-8">
      <p className="text-foreground/80">
        &ldquo;{t(`home.testimonials.items.${key}.quote`)}&rdquo;
      </p>
      <div className="mt-6 flex items-center gap-3">
        <span
          className="flex size-12 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white"
          style={{ backgroundColor: avatarBg }}
          aria-hidden
        >
          {initials}
        </span>
        <div>
          <p className="font-semibold">{name}</p>
          <p className="text-sm text-muted">{t(`home.testimonials.items.${key}.detail`)}</p>
        </div>
      </div>
    </div>
  );
}

// Reads the `lg` breakpoint via matchMedia rather than CSS alone, because how many cards make up
// a "page" (1 below lg, `desktopPerPage` at lg+) has to be known in JS to slice the source array
// and to auto-advance the right number of pages -- pure CSS can restyle a fixed DOM, not change
// how many cards are in it. Starts assuming mobile's single-card layout (matches the
// server-rendered guess) and corrects after mount, same pattern as this file's other
// window-dependent state. Shared by TestimonialsSlider and ServicesSlider.
function useResponsiveSliderPerPage(desktopPerPage: number) {
  const [perPage, setPerPage] = useState(1);
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 1024px)");
    const apply = () => setPerPage(mql.matches ? desktopPerPage : 1);
    apply();
    mql.addEventListener("change", apply);
    return () => mql.removeEventListener("change", apply);
  }, [desktopPerPage]);
  return perPage;
}

// Auto-advances through `items` `perPage` at a time, looping forever -- no manual controls by
// design (see SlidingGrid below), so this owns the whole rotation itself rather than exposing a
// setter.
function useAutoSlider<T>(items: T[], perPage: number, rotationMs: number) {
  const pageCount = Math.ceil(items.length / perPage);
  const [page, setPage] = useState(0);

  // perPage changing (crossing the lg breakpoint) can leave `page` pointing past the new,
  // shorter page count -- snap back to the start rather than render an empty page.
  useEffect(() => {
    setPage(0);
  }, [perPage]);

  useEffect(() => {
    if (pageCount <= 1) return;
    const id = setInterval(() => {
      setPage((p) => (p + 1) % pageCount);
    }, rotationMs);
    return () => clearInterval(id);
  }, [pageCount, rotationMs]);

  const visibleItems = items.slice(page * perPage, page * perPage + perPage);
  return { page, visibleItems };
}

// The sliding-page visual itself, shared by both auto-sliders on this page. Each tick slides the
// outgoing page left while the next slides in from the right (AnimatePresence mode="wait"
// finishes the exit before the enter starts, so they never overlap mid-transition). No manual
// prev/next or dots -- purely automatic, so there's nothing here for a user to operate.
function SlidingGrid({
  page,
  gridClassName,
  children,
}: {
  page: number;
  gridClassName: string;
  children: ReactNode;
}) {
  return (
    // `grid` here isn't a layout choice -- it's what lets the outgoing and incoming pages
    // occupy the exact same cell (both get grid-area: 1/1 below) so they overlap and slide past
    // each other in one continuous motion, with the grid auto-sizing the row to whichever is
    // taller. Without this, removing AnimatePresence's mode="wait" (below) to get that
    // continuous motion would leave two normal-flow siblings stacking vertically for the
    // duration of the crossover instead of overlapping.
    <div className="relative grid overflow-hidden">
      {/* initial={false}: this section usually mounts off-screen (below the fold), so the very
          first page should just appear in place instead of sliding in against nothing watching
          -- same reasoning as PhotoCarousel's identical prop. No mode="wait": the outgoing and
          incoming pages animate at the same time (old sliding out left while new simultaneously
          slides in from the right), rather than sequentially -- sequential meant a real gap
          where the outgoing page had already slid off and the incoming one hadn't slid in yet,
          i.e. the section going empty for a beat. A pure x move (no opacity) so this reads as an
          actual slide, not a cross-fade. */}
      <AnimatePresence initial={false}>
        <motion.div
          key={page}
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "-100%" }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          // willChange promotes this to its own GPU layer for the duration of the slide, so the
          // browser moves an already-rasterized layer on the compositor thread instead of
          // repainting it every frame. Photographic <Image> content (ServicesSlider) is much more
          // expensive to repaint per-frame than plain text (TestimonialsSlider), which is exactly
          // why the same transform/duration/easing here reads smoother on one than the other.
          style={{ gridArea: "1 / 1", willChange: "transform" }}
          className={gridClassName}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function TestimonialsSlider() {
  const perPage = useResponsiveSliderPerPage(3);
  const { page, visibleItems } = useAutoSlider(TESTIMONIALS, perPage, TESTIMONIAL_ROTATION_MS);
  return (
    <SlidingGrid page={page} gridClassName={`grid gap-6 ${perPage === 3 ? "lg:grid-cols-3" : "grid-cols-1"}`}>
      {visibleItems.map((item) => (
        <TestimonialCard key={item.key} item={item} />
      ))}
    </SlidingGrid>
  );
}

function WhatsAppCommunity() {
  return (
    <section>
      <WhatsAppCommunityCard />
    </section>
  );
}

function InstagramSection() {
  return (
    <section className="bg-background-alt">
      <div className="mx-auto max-w-[1440px] px-8 py-12 sm:py-16 lg:py-20">
        <InstagramTeaser compact />
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section id="contact">
      <CorporateCollabCard />
    </section>
  );
}
